import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { ArrowRight } from "lucide-react";
import { FEATURES_DATA, FEATURES_LIST } from "@/data/features";

export default function FeaturesSection() {
  const [ref, inView] = useInView();

  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-[#F8F9FB] relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E7E9EC] to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-sm uppercase tracking-widest text-brand-teal font-semibold mb-4">
            Everything you need
          </p>
          <h2
            data-testid="features-headline"
            className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight"
          >
            What's Inside NeuroNest
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {FEATURES_LIST.map((slug, i) => {
            const feature = FEATURES_DATA[slug];
            const Icon = feature.icon;
            const hasScreenshots = feature.screenshots.length > 0;

            return (
              <Link
                key={slug}
                to={`/features/${slug}`}
                data-testid={`feature-card-${i}`}
                className={`group bg-white rounded-3xl border border-[#E7E9EC] p-7 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.07)] transition-all duration-500 hover:-translate-y-1 block ${
                  inView
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${150 + i * 100}ms` }}
              >
                {/* Screenshot preview */}
                {hasScreenshots && (
                  <div className="mb-5 rounded-2xl overflow-hidden bg-[#F8F9FB]">
                    <img
                      src={feature.screenshots[0].url}
                      alt={`${feature.title} preview`}
                      className="w-full h-auto object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                )}

                <div
                  className={`w-11 h-11 rounded-xl ${feature.bgColor} flex items-center justify-center mb-5 ${hasScreenshots ? "" : "mt-0"}`}
                >
                  <Icon
                    size={20}
                    className={feature.iconColor}
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#5A6376] text-sm leading-relaxed mb-4">
                  {feature.description}
                </p>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-teal group-hover:gap-2.5 transition-all duration-300">
                  Learn more
                  <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
